import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import {
  formattedFamilySearchData,
  RawFamilySearchPersonResults,
} from '@ltvco/refresh-lib/fmt';
import { DataCollectionRecord } from '@ltvco/refresh-lib/hooks';
import {
  Stack,
  Text,
  useTheme,
  useMediaQuery,
  Box,
} from '@ltvco/refresh-lib/theme';
import {
  capitalizeEachWord,
  SPECIFIC_NAME_MAPPINGS,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import {
  Record,
  SerializedContactTeaserData,
  SerializedObituaryDetails,
} from '@ltvco/refresh-lib/v1';
import {
  SearchFilterModal,
  AdvancedSearchURLParams,
  CollectionType,
  FeatureBlockedModal,
  OptionType,
  TooltipContent,
  getSearchQueryParams,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { SearchpalChatbotPersistentView } from 'components/SearchpalChatbot';
import { SearchAssistantMobileContainer } from 'components/SearchpalChatbot/components';
import { SearchAssistantPersistantIcon } from 'components/SearchpalChatbot/components/SearchAssistantPersistantIcon';

import {
  ConsolidatedStickyContainer,
  PersonFiltersContainer,
  ResultsContainer,
  SearchbotSizebleContainer,
  SearchbotStickyContainer,
  SearchResultsGrid,
} from './components';
import { SearchPersonResultsProvider } from './context';
import { BirthIndexesFetcher } from './Fetchers/BirthIndexesFetcher';
import { CensusRecordsFetcher } from './Fetchers/CensusRecordsFetcher';
import { ContactFetcher } from './Fetchers/ContactFetcher';
import { DeathIndexesFetcher } from './Fetchers/DeathIndexesFetcher';
import { FamilySearchFetcher } from './Fetchers/FamilySearchFetcher';
import { MarriageIndexesFetcher } from './Fetchers/MarriageIndexesFetcher';
import { ObituaryFetcher } from './Fetchers/ObituaryFetcher';
import { PersonFetcher } from './Fetchers/PersonFetcher';
import { CollectionRecordsResultsContainer } from './ResultsContainers/CollectionRecordsResultsContainer';
import { ContactResultsContainer } from './ResultsContainers/ContactResultsContainer';
import { FamilySearchResultsContainer } from './ResultsContainers/FamilySearchResultsContainer';
import { ObituaryResultsContainer } from './ResultsContainers/ObituaryResultsContainer';
import { PersonResultsContainer } from './ResultsContainers/PersonResultsContainer';
import {
  CHATBOT_INTERACTION_TYPES,
  COURT_CASES,
} from './SearchFilter/constants';
import { viewTypes } from './SearchFilter/interfaces';
import { PersonFilters } from './SearchFilter/PersonFiltersSidebar';
import SearchHeaderCarousel from './SearchFilter/SearchHeader/SearchHeaderCarousel';
import { SearchHeaderDesktop } from './SearchFilter/SearchHeader/SearchHeaderDesktop';
import { getDurationBetweenTimestamps } from './SearchFilter/utils';

export function SearchPersonResultsv2() {
  const theme = useTheme();
  const { trackEventGA4 } = useContext(AppConfig);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { isBlocked, isHidden } = useSubscriptionAccessConfigContext();
  const {
    openFeatureBlockedModal,
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();

  const { isMobile, isMedium } = useScreenSize();
  const [searchParams] = useSearchParams();
  const [selectedOption, setSelectedOption] = useState<
    CollectionType | typeof COURT_CASES
  >(CollectionType.PUBLIC_RECORDS);
  const [manualSwitch, setManualSwitch] = useState<boolean>(false);
  const [parentSidebarCollapsed, setParentSidebarCollapsed] =
    useState<boolean>(true);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [initialNotificationVisible, setInitialNotificationVisible] =
    useState<boolean>(false);
  const [personResults, setPersonResults] = useState<Record[] | undefined>(
    undefined
  );
  const [obituaryResults, setObituaryResults] = useState<
    SerializedObituaryDetails | undefined
  >(undefined);
  const [deathIndexResults, setDeathIndexResults] = useState<
    DataCollectionRecord[] | undefined
  >(undefined);
  const [censusResults, setCensusResults] = useState<
    DataCollectionRecord[] | undefined
  >(undefined);
  const [marriageIndexesResults, setMarriageIndexesResults] = useState<
    DataCollectionRecord[] | undefined
  >(undefined);
  const [birthIndexResults, setBirthIndexResults] = useState<
    DataCollectionRecord[] | undefined
  >(undefined);
  const [contactResults, setContactResults] = useState<
    SerializedContactTeaserData | undefined
  >(undefined);
  const [familySearchResults, setFamilySearchResults] = useState<
    RawFamilySearchPersonResults | undefined
  >(undefined);
  const [chatbotInitialTime, setChatbotInitialTime] = useState<number | null>(
    null
  );
  const [currentView, setCurrentView] = useState<string>(viewTypes.GRID);
  const [searchType, setSearchType] = useState<string>('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);

  const isOnMobileCardInDesktopVariation = useFeatureIsOn('prop-3136');
  const isInMobileFiltersAndTabsVariation =
    useFeatureIsOn('prop-2960') && isMobile;

  const isInChatbotVariation = useFeatureIsOn('prop_2964');
  const isInSearchResultsVariation = useFeatureIsOn('RFRSH-4236');
  const isInChatbotVariationDesktop = isDesktop && isInChatbotVariation;
  const isInChatbotVariationMobile =
    (isMobile || isMedium) && isInChatbotVariation;
  const searchData: AdvancedSearchURLParams =
    getSearchQueryParams(searchParams);
  const { advancedSearch, source, fName, lName } = searchData;
  const userInExactAgeInputVariation = useFeatureIsOn('RTRN-1090');

  const [isFilterCollapsed, setIsFilterCollapsed] = useState<boolean>(false);
  const isSearchFilterOpen = isInSearchResultsVariation
    ? isFilterCollapsed
    : !isFilterCollapsed;
  // Detect if this is a Contact Pro search (only Contact report type selected)
  const isContactProSearch =
    advancedSearch &&
    source.length === 1 &&
    source[0] === CollectionType.CONTACTS;

  const personResultsCount: number = personResults?.length ?? 0;
  const obituaryResultsCount: number = obituaryResults?.length ?? 0;
  const deathIndexResultsCount: number = deathIndexResults?.length ?? 0;
  const censusResultsCount: number = censusResults?.length ?? 0;
  const marriageIndexesResultsCount: number =
    marriageIndexesResults?.length ?? 0;
  const birthIndexResultsCount: number = birthIndexResults?.length ?? 0;
  const contactResultsCount: number = contactResults?.total ?? 0;
  const familySearchResultsCount: number = familySearchResults
    ? formattedFamilySearchData(familySearchResults, fName, lName).length
    : 0;

  if (!searchData.fullName) {
    searchData.fullName = `${searchData.fName} ${searchData.lName}`;
  }

  const formatLength = (length: number | undefined): string => {
    const threshold = 99;
    if (length === undefined || length === null) {
      return '';
    }
    return length > threshold ? '99+' : length.toString();
  };

  const advancedSearchReportCounts = {
    people_search: formatLength(personResultsCount),
    family_search: formatLength(familySearchResultsCount),
    obituary: formatLength(obituaryResultsCount),
    birth_indexes: formatLength(birthIndexResultsCount),
    death_indexes: formatLength(deathIndexResultsCount),
    marriage_indexes: formatLength(marriageIndexesResultsCount),
    professional_contact: formatLength(contactResultsCount),
    census_records: formatLength(censusResultsCount),
    court_cases: '',
  };

  useEffect(() => {
    document.title = 'Search - BeenVerified';
  }, []);

  // Ensure filters are open for Contact Pro search (desktop and mobile)
  useEffect(() => {
    if (isContactProSearch && !isInSearchResultsVariation) {
      setIsFilterCollapsed(false);
      setParentSidebarCollapsed(false);
    }
  }, [isContactProSearch]);

  const fetchPerson =
    !advancedSearch ||
    source.includes(CollectionType.PUBLIC_RECORDS) ||
    isInSearchResultsVariation;
  const fetchObituary =
    !advancedSearch ||
    source.includes(CollectionType.OBITUARIES) ||
    isInSearchResultsVariation;
  const fetchAncestor =
    !advancedSearch ||
    source.includes(CollectionType.FAMILY_SEARCH) ||
    isInSearchResultsVariation;
  const fetchContact =
    !advancedSearch ||
    source.includes(CollectionType.CONTACTS) ||
    isInSearchResultsVariation;
  const fetchDeathIndex =
    !advancedSearch ||
    source.includes(CollectionType.DEATH_INDEXES) ||
    isInSearchResultsVariation;
  const fetchNebraskaBirthIndex =
    !advancedSearch ||
    source.includes(CollectionType.BIRTH_INDEXES) ||
    isInSearchResultsVariation;
  const fetchCensus =
    !advancedSearch ||
    source.includes(CollectionType.CENSUS_RECORDS) ||
    isInSearchResultsVariation;
  const fetcHMarriageIndex =
    !advancedSearch ||
    source.includes(CollectionType.MARRIAGE_INDEXES) ||
    isInSearchResultsVariation;

  const options: OptionType[] = [
    {
      label: 'Public Records',
      value: CollectionType.PUBLIC_RECORDS,
      length: formatLength(personResultsCount),
      visible: fetchPerson,
      tooltipContent: (
        <TooltipContent
          title="Public Records"
          description="Search for possible personal details, such as addresses, phone numbers, and associates."
        />
      ),
    },
    {
      label: 'FamilySearch',
      value: CollectionType.FAMILY_SEARCH,
      length: formatLength(familySearchResultsCount),
      visible: fetchAncestor,
      tooltipContent: (
        <TooltipContent
          title="FamilySearch"
          description="Trace relatives and explore family history through our partner, FamilySearch."
        />
      ),
    },
    {
      label: 'Professional Contact',
      value: CollectionType.CONTACTS,
      length: formatLength(contactResultsCount),
      visible: fetchContact,
      tooltipContent: (
        <TooltipContent
          title="Professional Contact"
          description="Find workplace details, professional contact, & career information about individuals."
        />
      ),
    },
    {
      label: 'Obituary',
      value: CollectionType.OBITUARIES,
      length: formatLength(obituaryResultsCount),
      visible: fetchObituary,
      tooltipContent: (
        <TooltipContent
          title="Obituary"
          description="Find published notices that share life stories and memorials."
        />
      ),
    },
    {
      label: 'Census Records',
      value: CollectionType.CENSUS_RECORDS,
      length: formatLength(censusResultsCount),
      visible: fetchCensus,
      tooltipContent: (
        <TooltipContent
          title="Census Records"
          description="Search official census data for possible historical details."
        />
      ),
    },
    {
      label: 'Death Indexes',
      value: CollectionType.DEATH_INDEXES,
      length: formatLength(deathIndexResultsCount),
      visible: fetchDeathIndex,
      tooltipContent: (
        <TooltipContent
          title="Death Index"
          description="See official records showing when and where someone may have passed away."
        />
      ),
    },
    {
      label: 'Birth Indexes',
      value: CollectionType.BIRTH_INDEXES,
      length: formatLength(birthIndexResultsCount),
      visible: fetchNebraskaBirthIndex,
      tooltipContent: (
        <TooltipContent
          title="Birth Index"
          description="Explore birth records that may help confirm family history."
        />
      ),
    },
    {
      label: 'Marriage Indexes',
      value: CollectionType.MARRIAGE_INDEXES,
      length: formatLength(marriageIndexesResultsCount),
      visible: fetcHMarriageIndex,
      tooltipContent: (
        <TooltipContent
          title="Marriage Index"
          description="Search official records for possible marriage details."
        />
      ),
    },
    {
      label: 'Court Cases',
      value: COURT_CASES,
      length: '',
      visible: true,
      tooltipContent: (
        <TooltipContent
          title="Court Cases"
          description="View records of US legal cases, including outcomes and related public details."
        />
      ),
    },
  ];

  let filteredOptions = options.filter(
    (option) => !isHidden(SPECIFIC_NAME_MAPPINGS[option.value.toLowerCase()])
  );

  const trackSearchResultsInteraction = (params: { [key: string]: string }) => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      ...params,
    });
  };

  const handleTabSwitch = (option: OptionType) => {
    trackSearchResultsInteraction({
      type: 'tab click',
      action: 'tab switch',
      section_name: option.label,
    });

    // Track null result click when clicking a tab with no results
    if (option.value !== COURT_CASES && option.length === '0') {
      trackSearchResultsInteraction({
        type: 'null_result_click',
        report_type: option.label,
      });
    }

    const optionValue = option.value.toLowerCase();

    if (isBlocked(SPECIFIC_NAME_MAPPINGS[optionValue])) {
      setSearchType(option.label);
      openFeatureBlockedModal();
      return;
    }

    if (option.value === COURT_CASES) {
      const trimmedName = searchData.fullName?.trim();
      const courtCasesUrl = trimmedName
        ? `/dashboard/court-cases/cases?query=${encodeURIComponent(
            `"${trimmedName}"`
          )}`
        : `/dashboard/court-cases/cases`;
      navigate(courtCasesUrl);
      return;
    }

    setSelectedOption(option.value as CollectionType);
    setManualSwitch(true);
  };

  const handleChatbotInteraction = (type: string) => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: `${type} drawer`,
    });
    if (type === CHATBOT_INTERACTION_TYPES.OPEN) {
      setChatbotInitialTime(Date.now());
      setIsChatbotOpen(true);
      setIsFilterCollapsed(isInSearchResultsVariation ? false : true);
    } else {
      setIsChatbotOpen(false);

      if (chatbotInitialTime) {
        trackEventGA4({
          event_name: 'search_results_interaction',
          type: 'chatbot session',
          value: getDurationBetweenTimestamps(
            chatbotInitialTime,
            Date.now()
          ).ms.toString(),
        });
        setChatbotInitialTime(null);
      }
    }
  };
  useEffect(() => {
    if (!manualSwitch && !isInMobileFiltersAndTabsVariation) {
      const hasContactInfo =
        searchData.title !== '' ||
        searchData.company !== '' ||
        searchData.industry !== '' ||
        searchData.level?.some((level) => level !== '');
      const hasObituaryInfo =
        searchData.birthYear !== '' ||
        searchData.birthMonth !== '' ||
        searchData.deathYear !== '' ||
        searchData.deathMonth !== '';

      const onlyBirthYearProvided =
        searchData.birthYear !== '' &&
        searchData.birthMonth === '' &&
        searchData.deathYear === '' &&
        searchData.deathMonth === '';
      const doNotDefaultToObituary =
        userInExactAgeInputVariation &&
        searchData.age !== '' &&
        onlyBirthYearProvided;

      if (
        hasContactInfo &&
        (source.length === 0 || source.includes(CollectionType.CONTACTS))
      ) {
        setSelectedOption(CollectionType.CONTACTS);
      } else if (
        searchData.type === 'obituary' ||
        (hasObituaryInfo &&
          (source.length === 0 || source.includes(CollectionType.OBITUARIES)))
      ) {
        if (doNotDefaultToObituary) return;
        setSelectedOption(CollectionType.OBITUARIES);
      }
    }
  }, [searchData, manualSwitch, selectedOption]);

  useEffect(() => {
    if (advancedSearch) {
      setSelectedOption(source[0] as CollectionType);
    }
  }, [searchParams.get('source')]);

  const fetchers = () => {
    return (
      <>
        {fetchPerson && (
          <PersonFetcher
            setPersonResults={setPersonResults}
            searchData={searchData}
          />
        )}
        {fetchObituary && (
          <ObituaryFetcher
            setObituaryResults={setObituaryResults}
            searchData={searchData}
          />
        )}
        {fetchNebraskaBirthIndex && (
          <BirthIndexesFetcher
            setBirthIndexResults={setBirthIndexResults}
            searchData={searchData}
          />
        )}
        {fetchDeathIndex && (
          <DeathIndexesFetcher
            setDeathIndexResults={setDeathIndexResults}
            searchData={searchData}
          />
        )}
        {fetchCensus && (
          <CensusRecordsFetcher
            setCensusResults={setCensusResults}
            searchData={searchData}
          />
        )}
        {fetcHMarriageIndex && (
          <MarriageIndexesFetcher
            setMarriageIndexesResults={setMarriageIndexesResults}
            searchData={searchData}
          />
        )}
        {fetchAncestor && (
          <FamilySearchFetcher
            setFamilySearchResults={setFamilySearchResults}
            searchData={searchData}
          />
        )}
        {fetchContact && (
          <ContactFetcher
            setContactResults={setContactResults}
            searchData={searchData}
          />
        )}
      </>
    );
  };

  const renderResults = () => {
    switch (selectedOption) {
      case CollectionType.FAMILY_SEARCH:
        return (
          <FamilySearchResultsContainer
            results={familySearchResults}
            searchData={searchData}
          />
        );
      case CollectionType.CONTACTS:
        return <ContactResultsContainer results={contactResults} />;
      case CollectionType.OBITUARIES:
        return (
          <ObituaryResultsContainer
            results={obituaryResults}
            searchData={searchData}
          />
        );
      case CollectionType.PUBLIC_RECORDS:
        return <PersonResultsContainer results={personResults} />;
      case CollectionType.DEATH_INDEXES:
        return (
          <CollectionRecordsResultsContainer
            tabName="Death Index"
            results={deathIndexResults}
          />
        );
      case CollectionType.CENSUS_RECORDS:
        return (
          <CollectionRecordsResultsContainer
            tabName="Census Records"
            results={censusResults}
          />
        );
      case CollectionType.MARRIAGE_INDEXES:
        return (
          <CollectionRecordsResultsContainer
            tabName="Marriage Index"
            results={marriageIndexesResults}
          />
        );
      case CollectionType.BIRTH_INDEXES:
        return (
          <CollectionRecordsResultsContainer
            tabName="Birth Index"
            results={birthIndexResults}
          />
        );
      case COURT_CASES:
        // This case should not be reached since we navigate away in handleTabSwitch
        return null;
      default:
        return null;
    }
  };

  const fullName = [searchData.fName, searchData.mName, searchData.lName]
    .filter(Boolean)
    .join(' ');

  const capitalizeFullName = capitalizeEachWord(
    fullName || searchData.fullName
  );

  const showHeaderTabs = () => {
    // Filter out Court Cases pill when viewing obituary results to prevent incorrect matches
    filteredOptions =
      selectedOption === CollectionType.OBITUARIES
        ? filteredOptions.filter((option) => option.value !== COURT_CASES)
        : filteredOptions;

    if (isDesktop || isInSearchResultsVariation) {
      return (
        <SearchHeaderDesktop
          options={filteredOptions}
          selectedOption={selectedOption}
          setSelectedOption={handleTabSwitch}
          searchData={searchData}
          isFilterCollapsed={!isSearchFilterOpen}
          setIsFilterCollapsed={toggleIsFilterCollapsed}
          isChatbotOpen={isChatbotOpen}
          setIsChatbotOpen={handleChatbotInteraction}
          initialNotificationVisible={initialNotificationVisible}
          setInitialNotificationVisible={setInitialNotificationVisible}
          reportCounts={advancedSearchReportCounts}
        />
      );
    }

    return (
      <SearchHeaderCarousel
        options={filteredOptions}
        selectedOption={selectedOption}
        setSelectedOption={handleTabSwitch}
        isInMobileFiltersAndTabsVariation={isInMobileFiltersAndTabsVariation}
        setParentSidebarCollapsed={
          isInMobileFiltersAndTabsVariation
            ? setParentSidebarCollapsed
            : undefined
        }
      />
    );
  };

  const personFiltersContainerStyles = () => {
    return {
      overflow: 'hidden',
      zIndex: 1,
      maxWidth: isFilterCollapsed ? '0' : '400px',
      minWidth: isFilterCollapsed ? '0' : '220px',
      width: isFilterCollapsed ? '0' : '24%',
      transition: 'all 0.3s ease-in-out',
      marginRight: isFilterCollapsed ? '0' : '24px',
      position: isDesktop ? 'sticky' : 'relative',
      top: isDesktop ? 48 : 0,
    };
  };

  const filterContainerStyles = () => {
    const styles = { width: '100%', maxWidth: '100%', top: 42 };
    if (isDesktop) {
      if (isFilterCollapsed) {
        return {
          ...styles,
          left: 1,
          transform: 'translateX(-100%)',
          transition: 'all 0.3s ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
          opacity: 0,
        };
      }

      return {
        ...styles,
        transform: 'translateX(0)',
        opacity: 1,
        position: 'sticky',
        transition: 'all 0.3s ease-in-out',
        pointerEvents: 'auto',
        zIndex: 1,
      };
    }
    return styles;
  };

  const toggleIsFilterCollapsed = (shouldCollapse: boolean) => {
    const action = shouldCollapse ? 'collapse' : 'expand';
    trackSearchResultsInteraction({ type: 'filter collapse', action });
    setIsFilterCollapsed(
      isInSearchResultsVariation ? !shouldCollapse : shouldCollapse
    );
  };

  useEffect(() => {
    if (isChatbotOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isChatbotOpen, isDesktop]);

  useEffect(() => {
    if (!parentSidebarCollapsed || (isSearchFilterOpen && isChatbotOpen)) {
      setIsChatbotOpen(false);
    }
  }, [parentSidebarCollapsed, isChatbotOpen, isSearchFilterOpen]);

  return (
    <>
      <Stack
        sx={{
          minHeight: 800,
          position: 'relative',
          backgroundColor: isInSearchResultsVariation
            ? theme.palette.background.default
            : theme.palette.background.paper,
          flexDirection: 'row',
        }}
      >
        <SearchResultsGrid>
          {isInSearchResultsVariation ? (
            <SearchFilterModal
              isOpen={isSearchFilterOpen}
              setIsOpen={(val) =>
                setIsFilterCollapsed(isInSearchResultsVariation ? val : !val)
              }
              reportCounts={advancedSearchReportCounts}
              userInExactAgeInputVariation={userInExactAgeInputVariation}
            />
          ) : (
            <PersonFiltersContainer sx={personFiltersContainerStyles()}>
              <ConsolidatedStickyContainer sx={filterContainerStyles()}>
                <PersonFilters
                  advancedSearchUrlParams={searchData}
                  onCollapse={() => toggleIsFilterCollapsed(true)}
                  parentSidebarCollapsed={
                    isInMobileFiltersAndTabsVariation || isContactProSearch
                      ? parentSidebarCollapsed
                      : undefined
                  }
                  setParentSidebarCollapsed={
                    isInMobileFiltersAndTabsVariation || isContactProSearch
                      ? setParentSidebarCollapsed
                      : undefined
                  }
                  userInExactAgeInputVariation={userInExactAgeInputVariation}
                  reportsAccordionCollapsed={isContactProSearch}
                />
              </ConsolidatedStickyContainer>
            </PersonFiltersContainer>
          )}
          <SearchPersonResultsProvider
            value={{
              currentView,
              setCurrentView,
              isOnMobileCardInDesktopVariation,
            }}
          >
            {isInSearchResultsVariation ? (
              <Stack
                sx={(theme) => ({
                  backgroundColor: theme.palette.background.default,
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                })}
              >
                <ResultsContainer
                  size={{ xs: 12 }}
                  sx={{ width: '95%', px: { xs: 2 } }}
                >
                  {showHeaderTabs()}
                  {fetchers()}
                  {renderResults()}
                </ResultsContainer>
              </Stack>
            ) : (
              <ResultsContainer
                size={{ xs: 12 }}
                sx={{
                  mt: {
                    xs: isInMobileFiltersAndTabsVariation ? 0 : 6.25,
                    md: 0,
                  },
                  width: '-webkit-fill-available',
                  padding: 2,
                }}
              >
                {showHeaderTabs()}
                {!isDesktop &&
                  (capitalizeFullName ? (
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      mr={1.25}
                      mb={2.5}
                      pt={2.5}
                    >
                      <Text
                        variant="h3"
                        sx={{
                          fontWeight: 'normal',
                          marginRight: '5px',
                          marginBottom: '0',
                        }}
                      >
                        Results for:
                      </Text>
                      <Text variant="h3" m={0}>
                        {capitalizeFullName}
                      </Text>
                    </Stack>
                  ) : (
                    <Box sx={{ height: 64, pt: 2.5, mb: 2.5 }} />
                  ))}
                {fetchers()}
                {renderResults()}
              </ResultsContainer>
            )}
          </SearchPersonResultsProvider>
          {isInChatbotVariationDesktop && (
            <SearchbotSizebleContainer
              isChatbotOpen={isChatbotOpen}
              theme={theme}
            >
              <SearchbotStickyContainer isChatbotOpen={isChatbotOpen}>
                <SearchpalChatbotPersistentView
                  resultsCount={personResultsCount}
                  isOpen={isChatbotOpen}
                  setInitialNotificationVisible={setInitialNotificationVisible}
                  setChatbotOpen={handleChatbotInteraction}
                  isDrawer={true}
                />
              </SearchbotStickyContainer>
            </SearchbotSizebleContainer>
          )}
        </SearchResultsGrid>
        {isInChatbotVariationMobile && (
          <SearchAssistantMobileContainer isChatbotOpen={isChatbotOpen}>
            <SearchpalChatbotPersistentView
              resultsCount={personResultsCount}
              isOpen={isChatbotOpen}
              setChatbotOpen={handleChatbotInteraction}
              setInitialNotificationVisible={setInitialNotificationVisible}
            />
          </SearchAssistantMobileContainer>
        )}
        {!isChatbotOpen &&
          parentSidebarCollapsed &&
          isInChatbotVariationMobile && (
            <SearchAssistantPersistantIcon
              initialNotificationVisible={initialNotificationVisible}
              setInitialNotificationVisible={setInitialNotificationVisible}
              capitalizeFullName={capitalizeFullName}
              setIsChatbotOpen={handleChatbotInteraction}
            />
          )}
      </Stack>

      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </>
  );
}
